import { Plus, Trash2 } from "lucide-react";
import { Children, useEffect, useRef } from "react";
import type { ReactNode } from "react";
import {
  numberOrNull,
  type ProductSection,
  type ShiftFormValidationIssue,
  validationMessage,
} from "./form-state";

const downtimeDepartments = [
  "Üretim",
  "Teknik",
  "Kalite",
  "Arge",
  "Depo",
  "Satınalma",
  "İdari",
  "Yönetim",
  "Diğer",
] as const;

import { instantToFormTime, formTimeToInstant } from "./form-time";

type Props = {
  productionDate?: string | null;
  product: ProductSection;
  speedUnit: string | null;
  validationIssues: readonly ShiftFormValidationIssue[];
  validationPrefix: string;
  onChange: (product: ProductSection) => void;
};
export function ShiftFormEvents({
  product,
  onChange,
  speedUnit,
  productionDate = null,
  validationIssues,
  validationPrefix,
}: Props) {
  const showMaintenanceTicket = product.downtimes.some(
    (item) =>
      item.responsibleDepartment.toLocaleLowerCase("tr-TR") === "teknik",
  );
  const update = <K extends "speedChanges" | "downtimes" | "productChanges">(
    key: K,
    index: number,
    entry: ProductSection[K][number],
  ) =>
    onChange({
      ...product,
      [key]: product[key].map((item, itemIndex) =>
        itemIndex === index ? entry : item,
      ),
    });
  const remove = <K extends "speedChanges" | "downtimes" | "productChanges">(
    key: K,
    index: number,
  ) =>
    onChange({
      ...product,
      [key]: product[key].filter((_item, itemIndex) => itemIndex !== index),
    });
  return (
    <>
      <PaperTable
        title="Hat Hızındaki Değişiklikler"
        tableClass="shift-speed-events"
        addLabel="Hız değişikliği ekle"
        columns={["Makina Hat Hızı / Adet", "Sebebi", "Bilgi Verilen Yetkili"]}
        validationField={`${validationPrefix}.speedChanges`}
        validationMessage={validationMessage(validationIssues, `${validationPrefix}.speedChanges`)}
        onAdd={() =>
          onChange({
            ...product,
            speedChanges: [
              ...product.speedChanges,
              {
                id: crypto.randomUUID(),
                occurredAt: null,
                newSpeed: null,
                unit: speedUnit,
                reason: "",
                informedPerson: null,
                notInformed: false,
              },
            ],
          })
        }
      >
        {product.speedChanges.map((item, index) => (
          <tr key={item.id ?? index} data-shift-diff-path={`${validationPrefix}.speedChanges.${index}`}>
            <td data-label="Makina Hat Hızı / Adet">
              <div className="shift-speed-input shift-table-pair">
                <input
                  data-shift-diff-path={`${validationPrefix}.speedChanges.${index}.newSpeed`}
                  aria-label="Makina Hat Hızı / Adet"
                  placeholder="Hızı yazın"
                  type="number"
                  inputMode="decimal"
                  min="0"
                  step="any"
                  disabled={!speedUnit}
                  value={item.newSpeed ?? ""}
                  onChange={(event) =>
                    update("speedChanges", index, {
                      ...item,
                      newSpeed: numberOrNull(event.target.value),
                      unit: speedUnit,
                    })
                  }
                />
                <span className="shift-event-unit">
                  {item.unit ?? speedUnit ?? "Önce hat seçin"}
                </span>
              </div>
            </td>
            <td data-label="Sebebi">
              <input
                  data-shift-diff-path={`${validationPrefix}.speedChanges.${index}.reason`}
                aria-label="Hız Değişikliği / Sebebi"
                placeholder="Nedenini yazın"
                value={item.reason}
                onChange={(event) =>
                  update("speedChanges", index, {
                    ...item,
                    reason: event.target.value,
                  })
                }
              />
            </td>
            <td data-label="Bilgi Verilen Yetkili">
              <input
                  data-shift-diff-path={`${validationPrefix}.speedChanges.${index}.informedPerson`}
                aria-label="Bilgi Verilen Yetkili"
                placeholder="Yetkilinin adını yazın"
                value={item.informedPerson ?? ""}
                onChange={(event) =>
                  update("speedChanges", index, {
                    ...item,
                    informedPerson: event.target.value || null,
                    notInformed: false,
                  })
                }
              />
            </td>
            <RemoveCell
              label="Hız değişikliğini kaldır"
              onClick={() => remove("speedChanges", index)}
            />
          </tr>
        ))}
      </PaperTable>
      <PaperTable
        title="Hat Duruş"
        tableClass="shift-downtime-events"
        addLabel="Duruş ekle"
        columns={[
          "Hat Duruş Başlangıç Saati",
          "Hat Duruş Bitiş Saati",
          "Sebebi",
          "Sorumlu Bölüm",
          ...(showMaintenanceTicket ? ["Teknik Bakım Talep Fiş No"] : []),
        ]}
        validationField={`${validationPrefix}.downtimes`}
        validationMessage={validationMessage(validationIssues, `${validationPrefix}.downtimes`)}
        onAdd={() =>
          onChange({
            ...product,
            downtimes: [
              ...product.downtimes,
              {
                id: crypto.randomUUID(),
                startedAt: null,
                endedAt: null,
                carriedToNextShift: false,
                reason: "",
                responsibleDepartment: "",
                maintenanceRequested: false,
                maintenanceTicketNo: null,
              },
            ],
          })
        }
      >
        {product.downtimes.map((item, index) => (
          <tr key={item.id ?? index} data-shift-diff-path={`${validationPrefix}.downtimes.${index}`}>
            <td data-label="Hat Duruş Başlangıç Saati">
              <input
                data-shift-diff-path={`${validationPrefix}.downtimes.${index}.startedAt`}
                aria-label="Hat Duruş Başlangıç Saati"
                type="time"
                disabled={!productionDate}
                value={instantToFormTime(item.startedAt)}
                onChange={(event) =>
                  update("downtimes", index, {
                    ...item,
                    startedAt: formTimeToInstant(
                      event.target.value,
                      productionDate,
                    ),
                  })
                }
              />
            </td>
            <td data-label="Hat Duruş Bitiş Saati">
              <input
                data-shift-diff-path={`${validationPrefix}.downtimes.${index}.endedAt`}
                aria-label="Hat Duruş Bitiş Saati"
                type="time"
                disabled={!productionDate}
                value={instantToFormTime(item.endedAt)}
                onChange={(event) =>
                  update("downtimes", index, {
                    ...item,
                    endedAt: formTimeToInstant(
                      event.target.value,
                      productionDate,
                      item.startedAt,
                    ),
                    carriedToNextShift: false,
                  })
                }
              />
            </td>
            <td data-label="Sebebi">
              <input
                data-shift-diff-path={`${validationPrefix}.downtimes.${index}.reason`}
                aria-label="Hat Duruş / Sebebi"
                placeholder="Duruş nedenini yazın"
                value={item.reason}
                onChange={(event) =>
                  update("downtimes", index, {
                    ...item,
                    reason: event.target.value,
                  })
                }
              />
            </td>
            <td data-label="Sorumlu Bölüm">
              <select
                data-shift-diff-path={`${validationPrefix}.downtimes.${index}.responsibleDepartment`}
                aria-label="Hat Duruş / Sorumlu Bölüm"
                value={item.responsibleDepartment}
                onChange={(event) =>
                  update("downtimes", index, {
                    ...item,
                    responsibleDepartment: event.target.value,
                  })
                }
              >
                <option value="">Bölüm seçin</option>
                {item.responsibleDepartment &&
                !downtimeDepartments.some(
                  (department) => department === item.responsibleDepartment,
                ) ? (
                  <option value={item.responsibleDepartment}>
                    {item.responsibleDepartment}
                  </option>
                ) : null}
                {downtimeDepartments.map((department) => (
                  <option key={department} value={department}>
                    {department}
                  </option>
                ))}
              </select>
            </td>
            {showMaintenanceTicket ? (
              <td data-label="Teknik Bakım Talep Fiş No">
                {item.responsibleDepartment.toLocaleLowerCase("tr-TR") ===
                "teknik" ? (
                  <input
                    data-shift-diff-path={`${validationPrefix}.downtimes.${index}.maintenanceTicketNo`}
                    aria-label="Teknik Bakım Talep Fiş No"
                    placeholder="Varsa fiş numarasını yazın"
                    value={item.maintenanceTicketNo ?? ""}
                    onChange={(event) =>
                      update("downtimes", index, {
                        ...item,
                        maintenanceTicketNo: event.target.value || null,
                      })
                    }
                  />
                ) : null}
              </td>
            ) : null}
            <RemoveCell
              label="Duruşu kaldır"
              onClick={() => remove("downtimes", index)}
            />
          </tr>
        ))}
      </PaperTable>
      <PaperTable
        title="Ürün Değişim"
        tableClass="shift-change-events"
        addLabel="Ürün değişimi ekle"
        columns={["Sebebi", "Adam Sayısı", "Sorumlu Bölüm"]}
        validationField={`${validationPrefix}.productChanges`}
        validationMessage={validationMessage(validationIssues, `${validationPrefix}.productChanges`)}
        onAdd={() =>
          onChange({
            ...product,
            productChanges: [
              ...product.productChanges,
              {
                id: crypto.randomUUID(),
                previousProduct: "",
                nextProduct: "",
                reason: "",
                participantCount: null,
                responsibleDepartment: "",
                restartAt: null,
                firstProductAt: null,
              },
            ],
          })
        }
      >
        {product.productChanges.map((item, index) => (
          <tr key={item.id ?? index} data-shift-diff-path={`${validationPrefix}.productChanges.${index}`}>
            <td data-label="Sebebi">
              <input
                data-shift-diff-path={`${validationPrefix}.productChanges.${index}.reason`}
                aria-label="Ürün Değişim / Sebebi"
                placeholder="Değişim nedenini yazın"
                value={item.reason}
                onChange={(event) =>
                  update("productChanges", index, {
                    ...item,
                    reason: event.target.value,
                  })
                }
              />
            </td>
            <td data-label="Adam Sayısı">
              <input
                data-shift-diff-path={`${validationPrefix}.productChanges.${index}.participantCount`}
                aria-label="Adam Sayısı"
                placeholder="Kişi sayısını yazın"
                type="number"
                inputMode="numeric"
                min="0"
                step="1"
                value={item.participantCount ?? ""}
                onChange={(event) =>
                  update("productChanges", index, {
                    ...item,
                    participantCount: numberOrNull(event.target.value),
                  })
                }
              />
            </td>
            <td data-label="Sorumlu Bölüm">
              <input
                data-shift-diff-path={`${validationPrefix}.productChanges.${index}.responsibleDepartment`}
                aria-label="Ürün Değişim / Sorumlu Bölüm"
                placeholder="Bölüm adını yazın"
                value={item.responsibleDepartment}
                onChange={(event) =>
                  update("productChanges", index, {
                    ...item,
                    responsibleDepartment: event.target.value,
                  })
                }
              />
            </td>
            <RemoveCell
              label="Ürün değişimini kaldır"
              onClick={() => remove("productChanges", index)}
            />
          </tr>
        ))}
      </PaperTable>
    </>
  );
}
function PaperTable({
  title,
  addLabel,
  columns,
  onAdd,
  children,
  tableClass,
  validationField,
  validationMessage: message,
}: {
  title: string;
  tableClass: string;
  validationField: string;
  validationMessage: string | null;
  addLabel: string;
  columns: string[];
  onAdd: () => void;
  children: ReactNode;
}) {
  const tableRef = useRef<HTMLTableElement>(null);
  const focusAddedRow = useRef(false);
  const rowCount = Children.count(children);
  useEffect(() => {
    if (!focusAddedRow.current) return;
    focusAddedRow.current = false;
    tableRef.current
      ?.querySelector<HTMLElement>("tbody tr:last-child input")
      ?.focus();
  }, [rowCount]);
  return (
    <section className="shift-paper-block" data-shift-validation={validationField} data-shift-diff-path={validationField} tabIndex={-1} aria-invalid={Boolean(message)}>
      <div className="shift-paper-heading">{title}</div>
      {message ? <p className="shift-section-error" role="alert">{message}</p> : null}
      <div className="shift-paper-scroll">
        <table
          ref={tableRef}
          className={`shift-paper-table shift-event-table ${tableClass}`}
        >
          <thead>
            <tr>
              {columns.map((column) => (
                <th key={column} scope="col">
                  {column}
                </th>
              ))}
              <th className="shift-table-remove" aria-label="Satır sil" />
            </tr>
          </thead>
          <tbody>{children}</tbody>
          <tfoot>
            <tr>
              <td colSpan={columns.length + 1} className="shift-table-add-cell">
                <button
                  type="button"
                  className="shift-table-add"
                  onClick={() => {
                    focusAddedRow.current = true;
                    onAdd();
                  }}
                >
                  <Plus size={15} aria-hidden="true" />
                  {addLabel}
                </button>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </section>
  );
}
function RemoveCell({
  label,
  onClick,
}: {
  label: string;
  onClick: () => void;
}) {
  return (
    <td className="shift-table-remove">
      <button type="button" aria-label={label} onClick={onClick}>
        <Trash2 size={15} />
      </button>
    </td>
  );
}
